<script lang="ts" setup>
import {onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {authenticationService, usersApi} from '@/container';
import pscHelper, {PSC_STATES} from '@/core/helpers/pscHelper.js';
import BaseAlert from '@/core/components/base/BaseAlert.vue';
import pscButtonUrl from '@/assets/psc-button.svg';
import electronHelper from '@/core/services/electronHelper';
import Icon from '@/core/components/icon/Icon.vue';
import SelfCheck from "@/login/components/SelfCheck.vue";
import LegacyUser from "@/core/interfaces/User";
import SentryService from "@/core/services/SentryService";
import {AxiosError} from "axios";
import retryWhileUnreachable from "@/core/http/retryWhileUnreachable";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";

const router = useRouter();
const route = useRoute();

const error = ref<string | null>(null);
const info = ref<string | null>(null);
const email = ref<string | null>(null);
const password = ref<string | null>(null);
const forgotPassMode = ref(false);
const showPassword = ref(false);
const checkingAuthentication = ref(true);
/** Whether the API has not answered yet, the check being retried meanwhile. */
const reachingServer = ref(false);

watch(
    () => route.query.redirect,
    (value) => {
        if (value) {
            router.push(value as string);
        }
    },
    {immediate: true}
);

/** On mounted, check if the user is already authenticated. If so, set the Sentry context and launch the app. */
onMounted(async () => {
    if (
        await authenticationService().hasToken()
        && !route.query?.redirect // If redirect query is set, block the app initialization.
    ) {
        const user = await currentUser();

        if (user && !route.query.failed) {
            launchApp(user.email);
        }
    }

    checkingAuthentication.value = false;
    email.value = import.meta.env.VITE_APP_LOGIN_EMAIL;
    password.value = import.meta.env.VITE_APP_LOGIN_PASSWORD;
});

const validate = () => {
    error.value = null;

    if (forgotPassMode.value && email.value) {
        authenticationService()
            .forgotPassword(email.value)
            .then((response: any) => {
                if (response.error === false) {
                    info.value = "Un mail de réinitialisation de mot de passe vient d'être envoyé.";
                } else {
                    error.value = response.message;
                }
            })
            .catch((err) => {
                error.value = err.response ? err.response.data.message : err;
            });
    } else {
        if (!email.value || !password.value) {
            error.value = 'Veuillez renseigner votre email et votre mot de passe.';
            return;
        }

        authenticationService()
            .login(email.value, password.value)
            .then((organization) => {
                if (organization) {
                    launchApp(email.value!);
                } else {
                    info.value =
                        'Pour profiter des services Apodis, veuillez télécharger l’application Apodis Pro disponible sur mobile et tablette, via les stores Android et Apple 🙂';
                }
            })
            .catch((err) => {
                if (err.response?.status === 401) {
                    error.value =
                        'Cet email et ce mot de passe ne correspondent à aucun compte. Vérifiez vos identifiants et réessayez.';
                } else {
                    error.value = err.response ? err.response.data.message : err;
                }
            });
    }
};

const keyUpEmail = () => {
    if (forgotPassMode.value) {
        validate();
    } else {
        document.getElementById('password')?.focus();
    }
};

const launchApp = (email: string) => {
    setSentryContext(email);

    router.push({
        name: electronHelper.isElectron() ? 'app.loading' : 'onboarding',
    });
};

const logWithProSanteConnect = () => {
    if (window.ipcRenderer) {
        window.ipcRenderer.logWithProSanteConnect(PSC_STATES.connect_login);
    } else {
        window.location.href = pscHelper.pscAuthLoginURL();
    }
};

const switchForgotPassMode = () => {
    forgotPassMode.value = !forgotPassMode.value;
};

/**
 * Get current user.
 *
 * Retried while the API is unreachable: at startup the network may not be up
 * yet, and giving up at once would show the login screen to a pharmacy whose
 * token is still valid.
 */
const currentUser = async (): Promise<LegacyUser | null> => {
    return retryWhileUnreachable(() => usersApi().me(), () => reachingServer.value = true)
        .catch((e: AxiosError) => {
            if (!e.response) {
                error.value = 'Impossible de joindre le serveur Apodis. Vérifiez la connexion internet de ce poste, puis relancez Apodis Connect.';
            } else if (![401, 403].includes(e.response.status)) {
                error.value = 'Maintenance en cours, veuillez réessayer dans quelques minutes.';
            }

            return null;
        })
        .finally(() => reachingServer.value = false);
};

/** Set Sentry context with user information */
const setSentryContext = (email: string) => {
    SentryService.setUser(email);
};
</script>

<template>
  <div v-if="checkingAuthentication" class="flex flex-col h-full v-full bg-primary-lightest">
    <template v-if="reachingServer">
      <BaseFlexSpinner />
      <p class="pb-12 text-center text-primary">
        Connexion au serveur Apodis…
      </p>
    </template>
  </div>
  <div v-else class="flex w-full items-center justify-center">
    <div class="flex items-center align-middle justify-center">
      <div class="max-w-md">
        <img
          class="mx-auto w-96"
          src="../../assets/images/logos/apodis-text-white.png"
          alt="Workflow"
        >
        <BaseAlert
          v-if="$route.query.unknownECPS"
          type=""
          title="Votre compte Pro Santé Connect n'est lié à aucun compte Apodis."
          class="mb-4"
        >
          Veuillez vous connecter avec votre email mot de passe si vous disposez d'un compte, puis nous vous
          inviterons
          à lier votre e-cps.
          <br><br>
          Si vous n'avez pas encore de compte Apodis, cliquez sur Créer mon compte.
        </BaseAlert>
        <BaseAlert
          v-if="$route.query.noAccount && $route.query.noPharmacist"
          type=""
          title="Votre compte Pro Santé Connect n'est lié à aucun compte Apodis."
          class="mb-4"
        >
          Rendez-vous sur l’application mobile Apodis Pro afin de créer un compte.
        </BaseAlert>
        <BaseAlert
          v-if="$route.query.noPharmacist && !$route.query.noAccount"
          type=""
          title="Vous ne pouvez pas utiliser cette application."
          class="mb-4"
        >
          Rendez-vous sur l’application Apodis PRO disponible sur smartphone et tablette
        </BaseAlert>

        <BaseAlert
          v-if="error"
          type="error"
          title=""
          class="mb-4"
        >
          {{ error }}
        </BaseAlert>

        <BaseAlert
          v-if="info"
          title=""
          class="text-center mb-2"
        >
          {{ info }}
        </BaseAlert>

        <div class="space-y-4 mb-12">
          <div>
            <div class="mb-2 text-s text-white text-center">
              M'identifier avec
            </div>
            <div class="flex flex-col items-center">
              <button
                type="button"
                @click="logWithProSanteConnect"
              >
                <img
                  alt="psc-button"
                  class="w-60"
                  :src="pscButtonUrl"
                >
              </button>
            </div>
            <div class="text-gray-400 text-center my-2">
              ou
            </div>
            <div>
              <div class="mb-4">
                <label
                  for="email-address"
                  class="sr-only"
                >Email</label>
                <input
                  id="email-address"
                  v-model="email"
                  type="email"
                  required
                  class="appearance-none w-full rounded relative block px-3 py-2 h-12
                    placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500
                    focus:border-indigo-500 focus:z-10 sm:text-sm shadow-lg bg-white"
                  placeholder="Email"
                  @keyup.enter="keyUpEmail"
                >
              </div>
            </div>
            <div v-if="!forgotPassMode">
              <label
                for="password"
                class="sr-only"
              >Mot de passe</label>
              <div class="relative">
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  class="appearance-none w-full rounded relative block px-3 py-2 h-12
                  placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500
                  focus:border-indigo-500 focus:z-10 sm:text-sm shadow-lg mb-1 bg-white"
                  placeholder="Mot de passe"
                  @keyup.enter="validate"
                >
                <div class="absolute inset-y-1/4 z-10 right-2">
                  <icon
                    :name="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    @click="showPassword = !showPassword"
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              class="focus:outline-none"
              @click="switchForgotPassMode"
            >
              <span class="text-sm text-gray-200 ">
                {{ forgotPassMode ? 'Se connecter' : 'Mot de passe oublié' }}
              </span>
            </button>
          </div>

          <div class="flex flex-col items-center">
            <button
              type="button"
              class="group relative flex justify-center mx-auto py-3 px-7 text-xl
                rounded-full bg-secondary text-primary-dark"
              @click="validate"
            >
              {{ forgotPassMode ? 'Réinitialiser mon mot de passe' : 'Me connecter' }}
            </button>
          </div>
        </div>

        <div class="flex items-center text-white gap-2">
          <p class="text-xl">
            Nouveau sur Apodis ?
          </p>
          <div class="flex flex-col items-center">
            <router-link
              to="/register"
              class="justify-center underline"
            >
              Créer un compte
            </router-link>
          </div>
        </div>
        <div class="absolute left-4 bottom-2">
          <SelfCheck v-if="electronHelper.isElectron()" />
        </div>
      </div>
    </div>
  </div>
</template>
